import {
  useQuery,
  gql,
  useLazyQuery,
  MutationHookOptions,
  useMutation,
  QueryHookOptions
} from '@apollo/client';
import { Exception } from '@jewelutions/core-common-domain';
import {
  GQLQueryPiece_GetPieceArgs,
  GQLQueryPiece_GetPiecesByProducerArgs,
  GQLPieceAssetConnection,
  GQLQueryPiece_GetPiecesByMerchantArgs,
  GQLQueryPiece_GetPiecesByPlatformArgs,
  GQLPieceAsset,
  GQLQueryPiece_ImportPieceModelCadFileJobsArgs,
  GQLJobSummary,
  GQLQueryPiece_ComputeAndImportPieceModelImageJobsArgs,
  GQLMutationPiece_ImportPieceModelCadFileArgs,
  GQLMutationPiece_ComputeAndImportPieceModelImageArgs,
  GQLMutationPiece_CreatePieceModelArgs,
  GQLMutationPiece_UpdatePieceModelArgs,
  GQLMutationPiece_CreatePieceArgs,
  GQLMutationPiece_UpdatePieceArgs,
  GQLQueryPiece_GetPiecesArgs
} from '@jewelutions/core-crm-server-api-client-domain';
import { JOB_SUMMARY_FIELDS } from './fragments/job';
import { PIECE_ASSET_FIELDS } from './fragments/piece';
import { OperationsHelper } from './OperationHelper';

export class PieceOperation {
  pieceAssetArrayFromResponse(data: any): GQLPieceAsset[] {
    if (data.Piece_getPieces !== undefined) {
      return data.Piece_getPieces;
    }
    return OperationsHelper.retrievePropertyFromGQLResponse(data, 'pieces');
  }

  pieceAssetFromResponse(data: any): GQLPieceAsset {
    if (data.Piece_getPiece !== undefined) {
      return data.Piece_getPiece;
    }
    throw new Exception(`Cannot handle input data pieceAsset`);
  }

  jobSummaryArrayFromResponse(data: any): GQLJobSummary[] {
    if (data === undefined) return [];
    if (data.Piece_importPieceModelCadFileJobs !== undefined) {
      return data.Piece_importPieceModelCadFileJobs;
    }
    if (data.Piece_computeAndImportPieceModelImageJobs !== undefined) {
      return data.Piece_computeAndImportPieceModelImageJobs;
    }
    throw new Exception(`Cannot handle input data job summary`);
  }

  getPieces(
    args: GQLQueryPiece_GetPiecesArgs,
    options: QueryHookOptions<GQLPieceAsset[], GQLQueryPiece_GetPiecesArgs> = {}
  ) {
    const query = gql`
      ${PIECE_ASSET_FIELDS}
      query Piece_getPieces($pieceIds: [ID!]!) {
        Piece_getPieces(pieceIds: $pieceIds) {
          ...PieceAssetFields
        }
      }
    `;
    return useQuery<GQLPieceAsset[], GQLQueryPiece_GetPiecesArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPiecesByPlatform(
    options: QueryHookOptions<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByPlatformArgs> = {}
  ) {
    const query = gql`
      ${PIECE_ASSET_FIELDS}
      query getPiecesByPlatform($pageSize: Int = 10) {
        Piece_getPiecesByPlatform(pageSize: $pageSize) {
          cursor
          hasMore
          pieces {
            ...PieceAssetFields
          }
        }
      }
    `;
    return useQuery<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByPlatformArgs>(query, {
      ...options
    });
  }

  getPiecesByProducer(
    args: GQLQueryPiece_GetPiecesByProducerArgs,
    options: QueryHookOptions<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByProducerArgs> = {}
  ) {
    const query = gql`
      ${PIECE_ASSET_FIELDS}
      query getPiecesByProducer($producerPartyId: ID!) {
        Piece_getPiecesByProducer(producerPartyId: $producerPartyId, pageSize: 10) {
          cursor
          hasMore
          pieces {
            ...PieceAssetFields
          }
        }
      }
    `;
    return useQuery<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByProducerArgs>(query, {
      ...options,
      variables: args
    });
  }

  getPiecesByMerchant(
    args: GQLQueryPiece_GetPiecesByMerchantArgs,
    options: QueryHookOptions<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByMerchantArgs> = {}
  ) {
    const query = gql`
      ${PIECE_ASSET_FIELDS}
      query getPiecesByMerchant($merchantPartyId: ID!, $pageSize: Int = 10) {
        Piece_getPiecesByMerchant(merchantPartyId: $merchantPartyId, pageSize: $pageSize) {
          cursor
          hasMore
          pieces {
            ...PieceAssetFields
          }
        }
      }
    `;
    return useQuery<GQLPieceAssetConnection, GQLQueryPiece_GetPiecesByMerchantArgs>(query, {
      ...options,
      variables: args
    });
  }

  createPieceModel(options: MutationHookOptions<GQLPieceAsset, GQLMutationPiece_CreatePieceModelArgs> = {}) {
    const mutation = gql`
      ${PIECE_ASSET_FIELDS}
      mutation createPieceModel($pieceId: ID!, $input: PieceModelInput!) {
        Piece_createPieceModel(pieceId: $pieceId, input: $input) {
          ...PieceAssetFields
        }
      }
    `;
    return useMutation<GQLPieceAsset, GQLMutationPiece_CreatePieceModelArgs>(mutation, options);
  }

  updatePieceModel(options: MutationHookOptions<GQLPieceAsset, GQLMutationPiece_UpdatePieceModelArgs> = {}) {
    const mutation = gql`
      ${PIECE_ASSET_FIELDS}
      mutation updatePieceModel($pieceId: ID!, $input: PieceModelInput!, $pieceModelId: ID!) {
        Piece_updatePieceModel(pieceId: $pieceId, pieceModelId: $pieceModelId, input: $input) {
          ...PieceAssetFields
        }
      }
    `;
    return useMutation<GQLPieceAsset, GQLMutationPiece_UpdatePieceModelArgs>(mutation, options);
  }

  createPiece(options: MutationHookOptions<GQLPieceAsset, GQLMutationPiece_CreatePieceArgs> = {}) {
    const mutation = gql`
      ${PIECE_ASSET_FIELDS}
      mutation createPiece($input: PieceAssetInput!) {
        Piece_createPiece(input: $input) {
          ...PieceAssetFields
        }
      }
    `;
    return useMutation<GQLPieceAsset, GQLMutationPiece_CreatePieceArgs>(mutation, options);
  }

  updatePiece(options: MutationHookOptions<GQLPieceAsset, GQLMutationPiece_UpdatePieceArgs> = {}) {
    const mutation = gql`
      ${PIECE_ASSET_FIELDS}
      mutation updatePiece($pieceId: ID!, $input: PieceAssetInput!) {
        Piece_updatePiece(pieceId: $pieceId, input: $input) {
          ...PieceAssetFields
        }
      }
    `;
    return useMutation<GQLPieceAsset, GQLMutationPiece_UpdatePieceArgs>(mutation, options);
  }

  getPiece(
    args: GQLQueryPiece_GetPieceArgs,
    options: QueryHookOptions<GQLPieceAsset, GQLQueryPiece_GetPieceArgs> = {}
  ) {
    const query = gql`
      ${PIECE_ASSET_FIELDS}
      query getPiece($pieceId: ID!) {
        Piece_getPiece(pieceId: $pieceId) {
          ...PieceAssetFields
        }
      }
    `;
    return useQuery<GQLPieceAsset, GQLQueryPiece_GetPieceArgs>(query, {
      ...options,
      variables: args
    });
  }

  importPieceModelCadFileJobs(
    args: GQLQueryPiece_ImportPieceModelCadFileJobsArgs,
    options: QueryHookOptions<GQLJobSummary[], GQLQueryPiece_ImportPieceModelCadFileJobsArgs> = {}
  ) {
    const query = gql`
      ${JOB_SUMMARY_FIELDS}
      query importPieceModelCadFileJobs($pieceId: ID!, $pieceModelId: ID!) {
        Piece_importPieceModelCadFileJobs(pieceId: $pieceId, pieceModelId: $pieceModelId) {
          ...JobSummaryFields
        }
      }
    `;
    return useQuery<GQLJobSummary[], GQLQueryPiece_ImportPieceModelCadFileJobsArgs>(query, {
      ...options,
      variables: args
    });
  }

  computeAndImportPieceModelImageJobs(
    args: GQLQueryPiece_ComputeAndImportPieceModelImageJobsArgs,
    options: QueryHookOptions<GQLJobSummary[], GQLQueryPiece_ComputeAndImportPieceModelImageJobsArgs> = {}
  ) {
    const query = gql`
      ${JOB_SUMMARY_FIELDS}
      query computeAndImportPieceModelImageJobs($pieceId: ID!, $pieceModelId: ID!) {
        Piece_computeAndImportPieceModelImageJobs(pieceId: $pieceId, pieceModelId: $pieceModelId) {
          ...JobSummaryFields
        }
      }
    `;
    return useQuery<GQLJobSummary[], GQLQueryPiece_ComputeAndImportPieceModelImageJobsArgs>(query, {
      ...options,
      variables: args
    });
  }

  importPieceModelCadFile(
    options: MutationHookOptions<Boolean, GQLMutationPiece_ImportPieceModelCadFileArgs> = {}
  ) {
    const mutation = gql`
      mutation Piece_importPieceModelCadFile($pieceId: ID!, $pieceModelId: ID!, $file: Upload!) {
        Piece_importPieceModelCadFile(pieceId: $pieceId, pieceModelId: $pieceModelId, file: $file)
      }
    `;
    return useMutation<Boolean, GQLMutationPiece_ImportPieceModelCadFileArgs>(mutation, options);
  }

  computeAndImportPieceModelImage(
    options: MutationHookOptions<Boolean, GQLMutationPiece_ComputeAndImportPieceModelImageArgs> = {}
  ) {
    const mutation = gql`
      mutation Piece_computeAndImportPieceModelImage(
        $pieceId: ID!
        $pieceConfigurations: [PieceConfigurationInput!]
      ) {
        Piece_computeAndImportPieceModelImage(pieceId: $pieceId, pieceConfigurations: $pieceConfigurations)
      }
    `;
    return useMutation<Boolean, GQLMutationPiece_ComputeAndImportPieceModelImageArgs>(mutation, options);
  }
}
